import { useCallback, useEffect, useRef, type CSSProperties, type ReactNode } from "react";

export type RevealVariant = "rise" | "editorial" | "fade" | "stagger";

const REVEAL_ANIMATIONS = new Set([
  "reveal-rise",
  "reveal-fade",
  "reveal-bubble",
  "reveal-editorial",
]);

export function useReveal<T extends HTMLElement>(steps = 0, variant: RevealVariant = "rise") {
  const cleanupRef = useRef<(() => void) | null>(null);

  const ref = useCallback((node: T | null) => {
    cleanupRef.current?.();
    cleanupRef.current = null;
    if (!node) return;
    if (!document.documentElement.classList.contains("motion")) return;
    if (!("IntersectionObserver" in window)) return;

    node.setAttribute("data-armed", "true");
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (!entry?.isIntersecting) return;
        node.classList.add("is-in");
        observer.disconnect();
      },
      { rootMargin: "0px 0px 18% 0px", threshold: 0 },
    );
    observer.observe(node);

    const settle = (event: AnimationEvent) => {
      const target = event.target;
      if (!(target instanceof HTMLElement)) return;
      if (!REVEAL_ANIMATIONS.has(event.animationName)) return;
      target.classList.add("is-settled");
    };
    node.addEventListener("animationend", settle);
    cleanupRef.current = () => {
      observer.disconnect();
      node.removeEventListener("animationend", settle);
    };
  }, []);

  useEffect(() => () => cleanupRef.current?.(), []);

  return {
    ref,
    className: `reveal reveal-${variant}`,
    style: { "--reveal-delay": `calc(${steps} * var(--motion-stagger))` } as CSSProperties,
  };
}

export default function Reveal({
  children,
  className,
  delay = 0,
  variant = "rise",
  as: Tag = "div",
}: {
  children: ReactNode;
  className?: string;
  delay?: number;
  variant?: RevealVariant;
  as?: "div" | "ul" | "p";
}) {
  const reveal = useReveal<HTMLElement>(delay, variant);
  return (
    <Tag
      ref={reveal.ref}
      className={[reveal.className, className].filter(Boolean).join(" ")}
      style={reveal.style}
    >
      {children}
    </Tag>
  );
}
